import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { tv, type VariantProps } from 'tailwind-variants';

const callout = tv({
  base: 'rounded-lg border',
  variants: {
    variant: {
      info: 'border-blue-200 bg-blue-50 text-blue-900',
      warning: 'border-yellow-200 bg-yellow-50 text-yellow-900',
      danger: 'border-red-500/50 bg-red-50 text-red-600',
      success: 'border-green-200 bg-green-50 text-green-900',
    },
    size: {
      normal: 'text-sm',
      compact: 'text-xs',
    },
    inline: {
      true: 'inline-flex items-center gap-1 border-none font-medium',
      false: 'w-full',
    },
  },
  compoundVariants: [
    {
      inline: true,
      size: 'normal',
      className: 'p-3 pr-4',
    },
    {
      inline: true,
      size: 'compact',
      className: 'p-2 pr-3',
    },
    {
      inline: false,
      size: 'normal',
      className: 'p-4',
    },
    {
      inline: false,
      size: 'compact',
      className: 'p-3',
    },
  ],
  defaultVariants: {
    variant: 'info',
    size: 'normal',
    inline: false,
  },
});

type CalloutProps = VariantProps<typeof callout> & {
  children: React.ReactNode;
  className?: string;
};

export const Callout = ({
  children,
  variant = 'info',
  size = 'normal',
  inline = false,
  className,
}: CalloutProps) => {
  if (inline) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={callout({ variant, size, inline, className })}
      >
        <InformationCircleIcon className="size-5 shrink-0" />
        <span className="leading-snug">{children}</span>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={callout({ variant, size, inline, className })}
    >
      <div className="flex items-center">
        <InformationCircleIcon className="mr-2 size-4 shrink-0" />
        <div className="leading-snug">{children}</div>
      </div>
    </div>
  );
};
